"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, Clock, ShieldCheck, ArrowRight, Ruler } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface BreedItem {
  id: string;
  name: string;
  size: string;
  weight: string;
  temperament: string;
  lifespan: string;
  energyLevel: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  summary: string;
}
const featuredBreeds: BreedItem[] = [{
  id: "golden-retriever",
  name: "Golden Retriever",
  size: "Large",
  weight: "55–75 lbs",
  temperament: "Gentle, eager, companionable",
  lifespan: "10–12 yrs",
  energyLevel: "High activity",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_golden_retriever.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_golden_retriever.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_golden_retriever.png-wvc-sizes",
  imageAlt: "Golden Retriever sitting attentively outdoors",
  summary: "Known for balanced temperament and high trainability, requiring consistent daily physical and mental engagement."
}, {
  id: "labrador-retriever",
  name: "Labrador Retriever",
  size: "Large",
  weight: "55–80 lbs",
  temperament: "Friendly, outgoing, versatile",
  lifespan: "11–13 yrs",
  energyLevel: "High activity",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_labrador_retriever.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_labrador_retriever.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_labrador_retriever.png-wvc-sizes",
  imageAlt: "Labrador Retriever looking alert in a natural landscape",
  summary: "An athletic breed with high endurance and a cooperative disposition, thriving in active family households."
}, {
  id: "french-bulldog",
  name: "French Bulldog",
  size: "Small",
  weight: "16–28 lbs",
  temperament: "Patient, affectionate, quiet",
  lifespan: "10–12 yrs",
  energyLevel: "Moderate activity",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_french_bulldog.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_french_bulldog.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_breed_french_bulldog.png-wvc-sizes",
  imageAlt: "French Bulldog resting calmly indoors",
  summary: "Compact and adaptable companion suited for apartment living, requiring attentive respiratory and thermal care."
}];
export default function Spotlight() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: shouldReduceMotion ? 0 : i * 0.1,
        ease: [0.16, 1, 0.3, 1] as const
      }
    })
  };
  return <section id="dog-breeds-spotlight" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Clinical Breed Profiles</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Featured dog breeds
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Explore temperament, physical profiles, and care needs to find the companion suited to your home and routine.
          </p>
        </div>

        {/* Breed Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-14">
          {featuredBreeds.map((breed, index) => <motion.div key={breed.id} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={cardVariants} className="flex">
              <Card className="flex flex-col justify-between w-full bg-card border border-border rounded-xl overflow-hidden shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40">
                <div>
                  {/* Breed Image Container */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img src={breed.imageSrc} data-wvc-srcset={breed.imageSrcset} data-wvc-sizes={breed.imageSizes} alt={breed.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs text-foreground px-2.5 py-1 rounded-md text-xs font-medium border border-border/80 flex items-center gap-1.5 shadow-2xs">
                      <Ruler className="size-3 text-muted-foreground" />
                      <span>{breed.size} &bull; {breed.weight}</span>
                    </div>
                  </div>

                  {/* Card Content Area */}
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                        {breed.name}
                      </h3>
                    </div>

                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {breed.summary}
                    </p>

                    {/* Structured Breed Metrics */}
                    <div className="border-t border-border pt-4 space-y-3">
                      <div className="flex items-start justify-between text-xs sm:text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5 font-medium">
                          <Clock className="size-3.5 text-muted-foreground shrink-0" />
                          Average lifespan
                        </span>
                        <span className="font-medium text-foreground text-right">{breed.lifespan}</span>
                      </div>

                      <div className="flex items-start justify-between text-xs sm:text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5 font-medium">
                          <Activity className="size-3.5 text-muted-foreground shrink-0" />
                          Activity need
                        </span>
                        <span className="font-medium text-foreground text-right">{breed.energyLevel}</span>
                      </div>

                      <div className="pt-2">
                        <div className="text-xs font-semibold text-foreground/80 uppercase tracking-wide mb-1.5">
                          Temperament traits
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground bg-muted/60 p-2.5 rounded-lg border border-border">
                          {breed.temperament}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Directory CTA */}
        <div className="text-center pt-2">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-semibold px-8 h-12 shadow-xs transition-[background-color,transform] duration-200">
            <Link to="/BreedsPage" className="inline-flex items-center gap-2">
              <span>View all breed guides</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Evidence-backed health screenings, nutrition targets, and exercise regimens for 120+ breeds.
          </p>
        </div>
      </div>
    </section>;
}